import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, functions, persistAuthProxySession, setAuthProxyEnabled } from '../firebase-client';
import { onAuthStateChanged, signOut as firebaseSignOut, signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { clearOfflineMediaCacheForUser } from '../utils/offlineMediaCache';
import { clearAdmissionOfflineData } from '../utils/admissionOfflineStore';
const SWITCH_SESSION_KEY = 'memberNest.accountSwitchSession';
const AUTH_BOOTSTRAP_TIMEOUT_MS = 10000;
const AuthContext = createContext({
  user: null,
  authLoading: true,
  signOut: () => {},
  actingAsChild: false,
  switchingAccount: false,
  switchToChildAccount: async () => {},
  switchBackToParentAccount: async () => {}
});
function readSwitchSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SWITCH_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}
function persistSwitchSession(value) {
  if (typeof window === 'undefined') return;
  try {
    if (!value) {
      window.sessionStorage.removeItem(SWITCH_SESSION_KEY);
      return;
    }
    window.sessionStorage.setItem(SWITCH_SESSION_KEY, JSON.stringify(value));
  } catch (err) {}
}
function getFamilySwitchIdentity(user, tokenResult) {
  const claims = tokenResult?.claims || {};
  const familyId = typeof claims.memberNestFamilyId === 'string'
    ? claims.memberNestFamilyId.trim()
    : '';
  const parentUid = typeof claims.memberNestParentUid === 'string'
    ? claims.memberNestParentUid.trim()
    : '';
  if (claims.memberNestMode !== 'child' || !user?.uid || !familyId || !parentUid) {
    return null;
  }
  return {
    familyId,
    parentUid,
    childUid: user.uid
  };
}
export function AuthProvider({
  children
}) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [switchSession, setSwitchSession] = useState(() => readSwitchSession());
  const [switchingAccount, setSwitchingAccount] = useState(false);
  useEffect(() => {
    let resolved = false;
    let authSequence = 0;
    const timeoutId = window.setTimeout(() => {
      if (resolved) return;
      setAuthLoading(false);
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);
    const unsub = onAuthStateChanged(auth, u => {
      const sequence = ++authSequence;
      resolved = true;
      window.clearTimeout(timeoutId);
      setUser(u);
      setSwitchSession(prev => {
        if (!prev) return prev;
        if (!u) {
          persistSwitchSession(null);
          return null;
        }
        if (u.uid === prev.childUid || u.uid === prev.parentUid) return prev;
        persistSwitchSession(null);
        return null;
      });
      if (u?.getIdTokenResult) {
        Promise.resolve(u.getIdTokenResult()).then(tokenResult => {
          if (sequence !== authSequence) return;
          const recoveredIdentity = getFamilySwitchIdentity(u, tokenResult);
          setSwitchSession(prev => {
            if (recoveredIdentity) {
              const recoveredSession = {
                ...(prev?.parentUid === recoveredIdentity.parentUid
                  && prev?.childUid === recoveredIdentity.childUid
                  && prev?.familyId === recoveredIdentity.familyId
                  ? prev
                  : {}),
                ...recoveredIdentity,
              };
              persistSwitchSession(recoveredSession);
              return recoveredSession;
            }
            if (prev?.childUid === u.uid) {
              persistSwitchSession(null);
              return null;
            }
            return prev;
          });
        }).catch(() => {
          // Keep a valid in-memory/session switch route if token inspection is
          // temporarily unavailable (for example during a network interruption).
        });
      }
      setAuthLoading(false);
    }, () => {
      resolved = true;
      window.clearTimeout(timeoutId);
      setUser(null);
      setAuthLoading(false);
    });
    return () => {
      resolved = true;
      window.clearTimeout(timeoutId);
      unsub();
    };
  }, []);
  const signOut = () => {
    if (user?.uid) Promise.resolve(clearOfflineMediaCacheForUser(user.uid)).catch(() => {});
    Promise.resolve(clearAdmissionOfflineData()).catch(() => {});
    persistSwitchSession(null);
    persistAuthProxySession(false);
    setAuthProxyEnabled(false);
    setSwitchSession(null);
    return firebaseSignOut(auth);
  };
  const switchToChildAccount = async ({
    familyId,
    childUid
  }) => {
    if (!user?.uid) throw new Error('You must be signed in.');
    const previousUserId = user.uid;
    setSwitchingAccount(true);
    try {
      const fn = httpsCallable(functions, 'createFamilyAccountSwitchTokens');
      const result = await fn({
        familyId,
        childUid
      });
      const data = result?.data || {};
      if (!data?.childCustomToken || !data?.parentCustomToken) {
        throw new Error('Switch tokens were not returned.');
      }
      const nextSession = {
        familyId: data.familyId || familyId,
        parentUid: data.parentUid || user.uid,
        childUid: data.childUid || childUid,
        parentCustomToken: data.parentCustomToken,
        switchedAt: new Date().toISOString()
      };
      persistSwitchSession(nextSession);
      setSwitchSession(nextSession);
      Promise.resolve(clearOfflineMediaCacheForUser(previousUserId)).catch(() => {});
      await signInWithCustomToken(auth, data.childCustomToken);
    } catch (err) {
      persistSwitchSession(null);
      setSwitchSession(null);
      throw err;
    } finally {
      setSwitchingAccount(false);
    }
  };
  const switchBackToParentAccount = async () => {
    const activeSession = switchSession || readSwitchSession();
    if (!activeSession?.parentUid || !activeSession?.childUid) {
      throw new Error('No parent session is available for return.');
    }
    setSwitchingAccount(true);
    try {
      if (user?.uid) Promise.resolve(clearOfflineMediaCacheForUser(user.uid)).catch(() => {});
      let parentCustomToken = activeSession.parentCustomToken || '';
      if (parentCustomToken) {
        try {
          await signInWithCustomToken(auth, parentCustomToken);
        } catch (err) {
          // Stored custom tokens expire. The active child identity can securely
          // request a fresh return token from its signed authentication claims.
          parentCustomToken = '';
        }
      }
      if (!parentCustomToken) {
        const fn = httpsCallable(functions, 'returnFromFamilyAccountSwitch');
        const result = await fn();
        parentCustomToken = result?.data?.parentCustomToken || '';
        if (!parentCustomToken) {
          throw new Error('A parent return token was not returned.');
        }
        await signInWithCustomToken(auth, parentCustomToken);
      }
      persistSwitchSession(null);
      setSwitchSession(null);
    } finally {
      setSwitchingAccount(false);
    }
  };
  const actingAsChild = Boolean(user?.uid && switchSession?.childUid && user.uid === switchSession.childUid);
  return <AuthContext.Provider value={{
    user,
    authLoading,
    signOut,
    actingAsChild,
    switchingAccount,
    switchToChildAccount,
    switchBackToParentAccount
  }}>
      {children}
    </AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
